import React, { useEffect, useMemo, useState } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  ChevronLeftIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';
import {
  apiService,
  BillingCycle,
  Product,
  ProductFeature,
  ProductListResponse,
  ProductPayload,
} from '../services/api';
import ConfirmModal from './ConfirmModal';

interface ProductManagementProps {
  onBack?: () => void;
}

interface FeatureLimitForm {
  featureId: number | '';
  value: string;
  note: string;
  isUnlimited?: boolean;
}

interface PricingHistoryForm {
  price: string;
  startDate: string;
  endDate: string;
}

interface ProductFormState {
  name: string;
  slug: string;
  description: string;
  basePrice: string;
  durationBilling: string;
  billingCycle: BillingCycle;
  isActive: boolean;
  pricingHistory: PricingHistoryForm[];
  featureLimits: FeatureLimitForm[];
}

type EditableProductField = Exclude<keyof ProductFormState, 'featureLimits' | 'pricingHistory'>;

type ModalMode = 'create' | 'edit';

const initialFormState: ProductFormState = {
  name: '',
  slug: '',
  description: '',
  basePrice: '',
  durationBilling: '1',
  billingCycle: BillingCycle.MONTHLY,
  isActive: true,
  pricingHistory: [],
  featureLimits: [],
};

const ProductManagement: React.FC<ProductManagementProps> = ({ onBack }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]); // For stats calculation
  const [pagination, setPagination] = useState<ProductListResponse['pagination']>({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'true' | 'false'>('all');
  const [cycleFilter, setCycleFilter] = useState<'all' | BillingCycle>('all');
  const [showDeleted, setShowDeleted] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>('create');
  const [formState, setFormState] = useState<ProductFormState>(initialFormState);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [saving, setSaving] = useState(false);
  const [featureOptions, setFeatureOptions] = useState<ProductFeature[]>([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    visible: boolean;
    type: 'create' | 'update' | 'delete' | null;
    product: Product | null;
    message: string;
  }>({
    visible: false,
    type: null,
    product: null,
    message: '',
  });

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== 'all') {
        params.isActive = statusFilter;
      }
      if (cycleFilter !== 'all') params.billingCycle = cycleFilter;
      params.includeDeleted = showDeleted ? 'true' : 'false';

      const response = await apiService.getProducts(params);
      setProducts(response.products || []);
      setPagination(response.pagination);

      // Fetch all products for stats calculation (without pagination)
      const statsParams: Record<string, string | number> = {
        page: 1,
        limit: 10000, // Large limit to get all
      };
      if (statusFilter !== 'all') {
        statsParams.isActive = statusFilter;
      }
      if (cycleFilter !== 'all') statsParams.billingCycle = cycleFilter;
      statsParams.includeDeleted = showDeleted ? 'true' : 'false';

      const statsResponse = await apiService.getProducts(statsParams);
      setAllProducts(statsResponse.products || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, searchTerm, statusFilter, cycleFilter, showDeleted]);

  useEffect(() => {
    const loadFeatures = async () => {
      setLoadingFeatures(true);
      try {
        const response = await apiService.getProductFeatures({ limit: 100 });
        setFeatureOptions(response.features || []);
      } catch (error) {
        console.error('Failed to fetch product features:', error);
      } finally {
        setLoadingFeatures(false);
      }
    };

    loadFeatures();
  }, []);

  const resetForm = () => {
    setFormState({
      ...initialFormState,
      pricingHistory: [],
      featureLimits: [],
    });
    setSelectedProduct(null);
  };

  const openCreateModal = () => {
    setModalMode('create');
    resetForm();
    setModalVisible(true);
  };

  const getFeatureById = (featureId: number | ''): ProductFeature | null => {
    if (!featureId) return null;
    return featureOptions.find((f) => f.id === featureId) || null;
  };

  const openEditModal = (product: Product) => {
    setModalMode('edit');
    setSelectedProduct(product);
    setFormState({
      name: product.name,
      slug: product.slug,
      description: product.description,
      basePrice: product.basePrice.toString(),
      durationBilling: product.durationBilling.toString(),
      billingCycle: product.billingCycle,
      isActive: product.isActive,
      pricingHistory:
        product.pricingHistory?.map((history) => ({
          price: history.price.toString(),
          startDate: history.startDate.split('T')[0], // Extract date part only
          endDate: history.endDate ? history.endDate.split('T')[0] : '',
        })) || [],
      featureLimits:
        product.featureLimits?.map((limit) => {
          return {
            featureId: limit.featureId,
            value: limit.value,
            note: limit.note || '',
            isUnlimited: false, // No longer used, values come from product_feature_value
          };
        }) || [],
    });
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    resetForm();
  };

  const addPricingHistory = () => {
    setFormState((prev) => ({
      ...prev,
      pricingHistory: [
        ...prev.pricingHistory,
        {
          price: '',
          startDate: '',
          endDate: '',
        },
      ],
    }));
  };

  const removePricingHistory = (index: number) => {
    setFormState((prev) => ({
      ...prev,
      pricingHistory: prev.pricingHistory.filter((_, i) => i !== index),
    }));
  };

  const handlePricingHistoryChange = (
    index: number,
    field: keyof PricingHistoryForm,
    value: string,
  ) => {
    setFormState((prev) => {
      const nextHistory = [...prev.pricingHistory];
      const current = nextHistory[index];
      if (!current) {
        return prev;
      }

      nextHistory[index] = {
        ...current,
        [field]: value,
      };
      return { ...prev, pricingHistory: nextHistory };
    });
  };

  const addFeatureLimit = () => {
    setFormState((prev) => ({
      ...prev,
      featureLimits: [
        ...prev.featureLimits,
        {
          featureId: '',
          value: '',
          note: '',
          isUnlimited: false,
        },
      ],
    }));
  };

  const removeFeatureLimit = (index: number) => {
    setFormState((prev) => ({
      ...prev,
      featureLimits: prev.featureLimits.filter((_, i) => i !== index),
    }));
  };

  const handleFeatureLimitChange = (
    index: number,
    field: keyof FeatureLimitForm,
    value: string | boolean,
  ) => {
    setFormState((prev) => {
      const nextLimits = [...prev.featureLimits];
      const current = nextLimits[index];
      if (!current) {
        return prev;
      }

      const updated: FeatureLimitForm = { ...current };

      if (field === 'featureId') {
        updated.featureId = value === '' ? '' : Number(value);
        // Reset value when feature changes
        updated.value = '';
      } else if (field === 'value') {
        updated.value = value as string;
      } else if (field === 'note') {
        updated.note = value as string;
      }

      nextLimits[index] = updated;
      return { ...prev, featureLimits: nextLimits };
    });
  };

  const handleFormChange = (field: EditableProductField, value: string | boolean) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const formatFeatureLimitValue = (limit: FeatureLimitForm): string => {
    // Value is already selected from product_feature_value, return as-is
    return limit.value;
  };

  const formatValueDisplay = (value: string, unit?: string | null): string => {
    if (!value) return '';

    // If value is boolean true, return "Unlimited"
    if (value === 'true') {
      return 'Unlimited';
    }

    // If value is boolean false, return "Disabled"
    if (value === 'false') {
      return 'Disabled';
    }

    // If value is numeric and unit exists, format with unit
    const num = parseFloat(value);
    if (!isNaN(num) && unit) {
      if (unit.includes('/bln') || unit.includes('x/bln')) {
        return `${num}${unit}`;
      }
      return `${num} ${unit}`;
    }

    // Return value as-is
    return value;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!formState.name || !formState.slug) {
      alert('Name and slug are required');
      return;
    }

    const trimmedDescription = formState.description.trim();
    const payload: ProductPayload = {
      name: formState.name.trim(),
      slug: formState.slug.trim(),
      ...(trimmedDescription && { description: trimmedDescription }),
      basePrice: Number(formState.basePrice),
      durationBilling: Number(formState.durationBilling),
      billingCycle: formState.billingCycle,
      isActive: formState.isActive,
      pricingHistory: formState.pricingHistory
        .map((history) => {
          if (!history.price || !history.startDate) {
            return null;
          }
          return {
            price: Number(history.price),
            startDate: history.startDate,
            endDate: history.endDate || undefined,
          };
        })
        .filter((history): history is NonNullable<typeof history> => history !== null),
      featureLimits: formState.featureLimits
        .map((limit) => {
          const parsedFeatureId =
            typeof limit.featureId === 'string'
              ? parseInt(limit.featureId, 10)
              : limit.featureId;

          if (Number.isNaN(parsedFeatureId) || !parsedFeatureId) {
            return null;
          }

          const feature = getFeatureById(parsedFeatureId);
          if (!feature) return null;

          // Validate: must have value selected
          if (!limit.value || limit.value.trim() === '') {
            return null;
          }

          const formattedValue = formatFeatureLimitValue(limit);

          return {
            featureId: parsedFeatureId,
            value: formattedValue,
            note: limit.note.trim() || undefined,
          };
        })
        .filter((limit): limit is NonNullable<typeof limit> => limit !== null),
    };

    // For create, save directly without confirmation
    if (modalMode === 'create') {
      setSaving(true);
      try {
        await apiService.createProduct(payload);
        closeModal();
        fetchProducts();
      } catch (error) {
        console.error('Failed to save product:', error);
      } finally {
        setSaving(false);
      }
      return;
    } else if (selectedProduct) {
      // Check if there are any changes
      const hasChanges = 
        formState.name !== selectedProduct.name ||
        formState.slug !== selectedProduct.slug ||
        formState.description !== (selectedProduct.description || '') ||
        formState.basePrice !== selectedProduct.basePrice.toString() ||
        formState.durationBilling !== selectedProduct.durationBilling.toString() ||
        formState.billingCycle !== selectedProduct.billingCycle ||
        formState.isActive !== selectedProduct.isActive;
      
      if (!hasChanges) {
        // No changes, just close the modal
        closeModal();
        return;
      }
      
      // Show confirmation dialog for update
      setConfirmModal({
        visible: true,
        type: 'update',
        product: selectedProduct,
        message: `Are you sure you want to update product "${selectedProduct.name}"?`,
      });
      return;
    }
  };

  const confirmSave = async () => {
    if (!confirmModal.type || confirmModal.type !== 'update' || !confirmModal.product) return;

    const trimmedDescription = formState.description.trim();
    const payload: ProductPayload = {
      name: formState.name.trim(),
      slug: formState.slug.trim(),
      ...(trimmedDescription && { description: trimmedDescription }),
      basePrice: Number(formState.basePrice),
      durationBilling: Number(formState.durationBilling),
      billingCycle: formState.billingCycle,
      isActive: formState.isActive,
      pricingHistory: formState.pricingHistory
        .map((history) => {
          if (!history.price || !history.startDate) {
            return null;
          }
          return {
            price: Number(history.price),
            startDate: history.startDate,
            endDate: history.endDate || undefined,
          };
        })
        .filter((history): history is NonNullable<typeof history> => history !== null),
      featureLimits: formState.featureLimits
        .map((limit) => {
          const parsedFeatureId =
            typeof limit.featureId === 'string'
              ? parseInt(limit.featureId, 10)
              : limit.featureId;

          if (Number.isNaN(parsedFeatureId) || !parsedFeatureId) {
            return null;
          }

          const feature = getFeatureById(parsedFeatureId);
          if (!feature) return null;

          // Validate: must have value selected
          if (!limit.value || limit.value.trim() === '') {
            return null;
          }

          const formattedValue = formatFeatureLimitValue(limit);

          return {
            featureId: parsedFeatureId,
            value: formattedValue,
            note: limit.note.trim() || undefined,
          };
        })
        .filter((limit): limit is NonNullable<typeof limit> => limit !== null),
    };
    
    setSaving(true);
    try {
      await apiService.updateProduct(confirmModal.product.id, payload);
      setConfirmModal({ visible: false, type: null, product: null, message: '' });
      closeModal();
      fetchProducts();
    } catch (error) {
      console.error('Failed to save product:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (product: Product) => {
    setConfirmModal({
      visible: true,
      type: 'delete',
      product,
      message: `Are you sure you want to delete product "${product.name}"?`,
    });
  };

  const confirmDelete = async () => {
    if (!confirmModal.product || confirmModal.type !== 'delete') return;
    
    try {
      await apiService.deleteProduct(confirmModal.product.id);
      setConfirmModal({ visible: false, type: null, product: null, message: '' });
      fetchProducts();
    } catch (error) {
      console.error('Failed to delete product:', error);
      alert('Failed to delete product. Please try again.');
    }
  };

  const handleRestore = async (product: Product) => {
    try {
      await apiService.restoreProduct(product.id);
      fetchProducts();
    } catch (error) {
      console.error('Failed to restore product:', error);
    }
  };

  const handleToggleActive = async (product: Product) => {
    try {
      await apiService.updateProduct(product.id, { isActive: !product.isActive });
      fetchProducts();
    } catch (error) {
      console.error('Failed to update product status:', error);
    }
  };

  const formatCurrency = (value: number) => {
    // Ensure value is a valid number
    const numValue = typeof value === 'number' ? value : parseFloat(String(value || 0));
    if (isNaN(numValue) || !isFinite(numValue)) {
      return 'Rp 0';
    }
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numValue);
  };

  const stats = useMemo(() => {
    // Use allProducts for accurate stats calculation
    const productsForStats = allProducts.length > 0 ? allProducts : products;

    if (!productsForStats.length) {
      return { active: 0, inactive: 0, totalRevenue: 0 };
    }

    const active = productsForStats.filter((p) => p.isActive && !p.softDelete).length;
    const inactive = productsForStats.filter((p) => !p.isActive && !p.softDelete).length;

    // Calculate total revenue - ensure basePrice is converted to number
    const totalRevenue = productsForStats.reduce((sum, product) => {
      // Handle Decimal type from Prisma (might be string or number)
      let price: number;
      if (typeof product.basePrice === 'number') {
        price = product.basePrice;
      } else if (typeof product.basePrice === 'string') {
        price = parseFloat(product.basePrice);
      } else {
        price = 0;
      }

      // Ensure valid number
      if (isNaN(price) || !isFinite(price)) {
        price = 0;
      }

      return sum + price;
    }, 0);

    return { active, inactive, totalRevenue };
  }, [products, allProducts]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleStatusFilterChange = (value: 'all' | 'true' | 'false') => {
    setStatusFilter(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleCycleFilterChange = (value: 'all' | BillingCycle) => {
    setCycleFilter(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (direction: 'prev' | 'next') => {
    setPagination((prev) => {
      const maxPage = Math.max(1, prev.totalPages || 1);
      const nextPage =
        direction === 'prev' ? Math.max(1, prev.page - 1) : Math.min(maxPage, prev.page + 1);
      return { ...prev, page: nextPage };
    });
  };

  const handlePageSizeChange = (size: number) => {
    setPagination((prev) => ({ ...prev, page: 1, limit: size }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Master Data Product</h1>
            <p className="text-sm text-gray-500">Manage product plans and pricing</p>
          </div>
        </div>
        <button
          onClick={openCreateModal}
          className="bg-imola-primary hover:bg-imola-primary/80 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          New Product
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Active Products</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{stats.active}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Inactive Products</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{stats.inactive}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Total Base Price (IDR)</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {formatCurrency(stats.totalRevenue)}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name or slug..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-imola-primary focus:border-imola-primary sm:text-sm"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value as 'all' | 'true' | 'false')}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
            >
              <option value="all">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Billing:</label>
            <select
              value={cycleFilter}
              onChange={(e) => handleCycleFilterChange(e.target.value as 'all' | BillingCycle)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
            >
              <option value="all">All</option>
              <option value={BillingCycle.MONTHLY}>Monthly</option>
              <option value={BillingCycle.YEARLY}>Yearly</option>
            </select>
          </div>
          <label className="inline-flex items-center text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => {
                setShowDeleted(e.target.checked);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="mr-2"
            />
            Show deleted
          </label>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Page Size:</label>
            <select
              value={pagination.limit}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Slug
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Base Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Billing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deleted
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    No products found
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 text-left">{product.name}</div>
                      <div className="text-xs text-gray-500 text-left">
                        {new Date(product.updatedAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      <span className="text-sm text-gray-700 font-mono bg-gray-50 px-2 py-1 rounded border">
                        {product.slug}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                      {formatCurrency(product.basePrice)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                      {product.durationBilling} /{' '}
                      {product.billingCycle === BillingCycle.MONTHLY ? 'Month' : 'Year'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      <button
                        onClick={() => handleToggleActive(product)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${product.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                          }`}
                      >
                        {product.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${product.softDelete
                          ? 'bg-red-100 text-red-800'
                          : 'bg-blue-50 text-blue-800'
                          }`}
                      >
                        {product.softDelete ? 'Deleted' : 'Available'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openEditModal(product)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="Edit"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        {product.softDelete ? (
                          <button
                            onClick={() => handleRestore(product)}
                            className="text-green-600 hover:text-green-900 p-1"
                            title="Restore"
                          >
                            <ArrowUturnLeftIcon className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleDelete(product)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Delete"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.total > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="hidden sm:block">
              <p className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">
                  {Math.min((pagination.page - 1) * pagination.limit + 1, pagination.total)}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                of <span className="font-medium">{pagination.total}</span> results
              </p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange('prev')}
                disabled={pagination.page === 1}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange('next')}
                disabled={pagination.page >= (pagination.totalPages || 1)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {modalVisible && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={closeModal}></div>
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between border-b px-4 sm:px-6 py-4 bg-gray-50">
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {modalMode === 'create' ? 'Create Product' : 'Edit Product'}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {modalMode === 'create'
                      ? 'Define pricing and billing details for the new product'
                      : 'Update product information'}
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="ml-4 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-200"
                  aria-label="Close"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="overflow-y-auto flex-1">
                <form id="product-form" onSubmit={handleSubmit} className="px-4 sm:px-6 py-6 space-y-6">
                  {/* Basic Information Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Basic Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formState.name}
                          onChange={(e) => handleFormChange('name', e.target.value)}
                          className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-imola-primary focus:border-imola-primary transition-colors"
                          placeholder="Enter product name"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Slug <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formState.slug}
                          onChange={(e) => handleFormChange('slug', e.target.value)}
                          className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-imola-primary focus:border-imola-primary transition-colors"
                          placeholder="product-slug"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description <span className="text-gray-400 text-xs font-normal">(optional)</span>
                      </label>
                      <textarea
                        value={formState.description}
                        onChange={(e) => handleFormChange('description', e.target.value)}
                        rows={3}
                        className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-imola-primary focus:border-imola-primary transition-colors resize-none"
                        placeholder="Enter product description"
                      />
                    </div>
                  </div>

                  {/* Pricing & Billing Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Pricing & Billing</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Base Price (IDR) <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">Rp</span>
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={formState.basePrice}
                            onChange={(e) => handleFormChange('basePrice', e.target.value)}
                            className="block w-full border border-gray-300 rounded-md pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-imola-primary focus:border-imola-primary transition-colors"
                            placeholder="0"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Duration <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          min={1}
                          value={formState.durationBilling}
                          onChange={(e) => handleFormChange('durationBilling', e.target.value)}
                          className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-imola-primary focus:border-imola-primary transition-colors"
                          placeholder="1"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Billing Cycle <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formState.billingCycle}
                          onChange={(e) =>
                            handleFormChange('billingCycle', e.target.value as BillingCycle)
                          }
                          className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-imola-primary focus:border-imola-primary transition-colors"
                          required
                        >
                          <option value={BillingCycle.MONTHLY}>Monthly</option>
                          <option value={BillingCycle.YEARLY}>Yearly</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Pricing History Section */}
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">Pricing History</h3>
                        <p className="text-xs text-gray-500 mt-1">
                          Track price changes over time for this product
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={addPricingHistory}
                        className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border border-imola-primary text-imola-primary bg-white hover:bg-imola-primary hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <PlusIcon className="w-4 h-4 mr-2" />
                        Add Pricing History
                      </button>
                    </div>

                    {formState.pricingHistory.length === 0 ? (
                      <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                        <p className="text-sm text-gray-500">
                          No pricing history added. Click &quot;Add Pricing History&quot; to track price changes.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {formState.pricingHistory.map((history, index) => (
                          <div
                            key={`pricing-history-${index}`}
                            className="border border-gray-200 rounded-lg p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                          >
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                              <div className="lg:col-span-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Price (IDR) <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">Rp</span>
                                  <input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={history.price}
                                    onChange={(e) => handlePricingHistoryChange(index, 'price', e.target.value)}
                                    className="block w-full border border-gray-300 rounded-md pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-imola-primary focus:border-imola-primary transition-colors"
                                    placeholder="0"
                                    required
                                  />
                                </div>
                              </div>
                              <div className="lg:col-span-3">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Start Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="date"
                                  value={history.startDate}
                                  onChange={(e) => handlePricingHistoryChange(index, 'startDate', e.target.value)}
                                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-imola-primary focus:border-imola-primary transition-colors"
                                  required
                                />
                              </div>
                              <div className="lg:col-span-3">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  End Date <span className="text-gray-400 text-xs">(optional)</span>
                                </label>
                                <input
                                  type="date"
                                  value={history.endDate}
                                  onChange={(e) => handlePricingHistoryChange(index, 'endDate', e.target.value)}
                                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-imola-primary focus:border-imola-primary transition-colors"
                                />
                              </div>
                              <div className="lg:col-span-2 flex items-end justify-end pb-1">
                                <button
                                  type="button"
                                  onClick={() => removePricingHistory(index)}
                                  className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                                  title="Remove pricing history"
                                  aria-label="Remove"
                                >
                                  <TrashIcon className="w-5 h-5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Feature Limits Section */}
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">Feature Limits</h3>
                        <p className="text-xs text-gray-500 mt-1">
                          Define per-feature caps and restrictions for this product
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={addFeatureLimit}
                        disabled={loadingFeatures || featureOptions.length === 0}
                        className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border border-imola-primary text-imola-primary bg-white hover:bg-imola-primary hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <PlusIcon className="w-4 h-4 mr-2" />
                        Add Feature Limit
                      </button>
                    </div>

                    {loadingFeatures ? (
                      <div className="text-center py-8">
                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-imola-primary"></div>
                        <p className="text-sm text-gray-500 mt-2">Loading feature catalog...</p>
                      </div>
                    ) : formState.featureLimits.length === 0 ? (
                      <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                        <p className="text-sm text-gray-500">
                          {featureOptions.length === 0
                            ? 'No product features available yet. Please create features first.'
                            : 'No feature limits added. Click "Add Feature Limit" to configure caps.'}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {formState.featureLimits.map((limit, index) => (
                          <div
                            key={`feature-limit-${index}`}
                            className="border border-gray-200 rounded-lg p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                          >
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                              <div className="lg:col-span-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Feature <span className="text-red-500">*</span>
                                </label>
                                <select
                                  value={limit.featureId}
                                  onChange={(e) => handleFeatureLimitChange(index, 'featureId', e.target.value)}
                                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-imola-primary focus:border-imola-primary transition-colors"
                                >
                                  <option value="">Select feature</option>
                                  {featureOptions.map((feature) => (
                                    <option key={feature.id} value={feature.id}>
                                      {feature.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="lg:col-span-5">
                                {(() => {
                                  // When editing, try to get feature from selectedProduct's featureLimits first
                                  // (which includes values from backend), then fallback to featureOptions
                                  let feature: ProductFeature | null = null;
                                  if (selectedProduct && modalMode === 'edit') {
                                    const limitFromProduct = selectedProduct.featureLimits?.find(
                                      (fl) => fl.featureId === limit.featureId
                                    );
                                    if (limitFromProduct?.feature) {
                                      feature = limitFromProduct.feature;
                                    }
                                  }

                                  // Fallback to featureOptions if not found
                                  if (!feature) {
                                    feature = getFeatureById(limit.featureId);
                                  }

                                  if (!feature) {
                                    return (
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Value
                                        </label>
                                        <input
                                          type="text"
                                          disabled
                                          className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-gray-100 cursor-not-allowed"
                                          placeholder="Select feature first"
                                        />
                                      </div>
                                    );
                                  }

                                  // Get values from product_feature_value
                                  // At this point, feature is guaranteed to be non-null
                                  const featureUnit = feature.unit;
                                  const featureValues = feature.values || [];

                                  if (featureValues.length === 0) {
                                    return (
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Value <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                          type="text"
                                          value={limit.value}
                                          onChange={(e) =>
                                            handleFeatureLimitChange(index, 'value', e.target.value)
                                          }
                                          className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-imola-primary focus:border-imola-primary transition-colors"
                                          placeholder="Enter value (no predefined values)"
                                        />
                                        <p className="mt-1 text-xs text-gray-500">
                                          No predefined values available for this feature. Enter value manually.
                                        </p>
                                      </div>
                                    );
                                  }

                                  // Check if current value exists in feature values
                                  const currentValueExists = featureValues.some((fv) => fv.value === limit.value);

                                  return (
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Value <span className="text-red-500">*</span>
                                        {featureUnit && (
                                          <span className="text-gray-400 text-xs font-normal ml-1">
                                            (Unit: {featureUnit})
                                          </span>
                                        )}
                                      </label>
                                      <select
                                        value={limit.value}
                                        onChange={(e) =>
                                          handleFeatureLimitChange(index, 'value', e.target.value)
                                        }
                                        className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-imola-primary focus:border-imola-primary transition-colors"
                                        required
                                      >
                                        <option value="">Select value</option>
                                        {featureValues.map((fv) => {
                                          const displayValue = formatValueDisplay(fv.value, featureUnit);
                                          return (
                                            <option key={fv.id} value={fv.value}>
                                              {displayValue}
                                            </option>
                                          );
                                        })}
                                        {/* Show current value if it doesn't exist in predefined values (for backward compatibility) */}
                                        {!currentValueExists && limit.value && (
                                          <option value={limit.value}>
                                            {formatValueDisplay(limit.value, featureUnit)} (Current)
                                          </option>
                                        )}
                                      </select>
                                      <p className="mt-1 text-xs text-gray-500">
                                        Select from predefined values for this feature
                                        {!currentValueExists && limit.value && (
                                          <span className="text-yellow-600 ml-1">
                                            • Current value may not be in predefined list
                                          </span>
                                        )}
                                      </p>
                                    </div>
                                  );
                                })()}
                              </div>
                              <div className="lg:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Note <span className="text-gray-400 text-xs">(optional)</span>
                                </label>
                                <input
                                  type="text"
                                  value={limit.note}
                                  onChange={(e) => handleFeatureLimitChange(index, 'note', e.target.value)}
                                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-imola-primary focus:border-imola-primary transition-colors"
                                  placeholder="Internal note"
                                />
                              </div>
                              <div className="lg:col-span-1 flex items-end justify-end pb-1">
                                <button
                                  type="button"
                                  onClick={() => removeFeatureLimit(index)}
                                  className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                                  title="Remove feature limit"
                                  aria-label="Remove"
                                >
                                  <TrashIcon className="w-5 h-5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Status Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Status</h3>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isActive"
                        checked={formState.isActive}
                        onChange={(e) => handleFormChange('isActive', e.target.checked)}
                        className="w-4 h-4 text-imola-primary border-gray-300 rounded focus:ring-imola-primary"
                      />
                      <label htmlFor="isActive" className="ml-2 text-sm font-medium text-gray-700 cursor-pointer">
                        Active product
                      </label>
                    </div>
                  </div>
                </form>
              </div>

              {/* Footer */}
              <div className="border-t bg-gray-50 px-4 sm:px-6 py-4 flex flex-col sm:flex-row justify-end gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="product-form"
                  disabled={saving}
                  className="px-4 py-2 bg-imola-primary text-white rounded-md text-sm font-medium hover:bg-imola-primary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    modalMode === 'create' ? 'Create Product' : 'Update Product'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        visible={confirmModal.visible}
        title={
          confirmModal.type === 'update' ? 'Confirm Update' :
          confirmModal.type === 'delete' ? 'Confirm Delete' : 'Confirm'
        }
        message={confirmModal.message}
        confirmText={
          confirmModal.type === 'update' ? 'Update' :
          confirmModal.type === 'delete' ? 'Delete' : 'Confirm'
        }
        cancelText="Cancel"
        confirmButtonClass={
          confirmModal.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-imola-primary hover:bg-imola-primary/80'
        }
        onConfirm={() => {
          if (confirmModal.type === 'delete') {
            confirmDelete();
          } else if (confirmModal.type === 'update') {
            confirmSave();
          }
        }}
        onCancel={() => setConfirmModal({ visible: false, type: null, product: null, message: '' })}
      />
    </div>
  );
};

export default ProductManagement;

